import { useSpring, easings } from '@react-spring/web';
import { useEffect } from 'react';

import { AnimatedDiv } from '@/utils/animated-div';

export function PulsingDot() {
  // Inner dot fade-in animation (0-600ms) - reset on every mount
  const [dotSpring, dotApi] = useSpring(() => ({
    from: { opacity: 0, scale: 1 },
  }));

  // Outer ring pulsing animation using API pattern
  // Ring sizes: 20px (large) and 14px (small)
  const [ringSpring, ringApi] = useSpring(() => ({
    from: { width: 20, height: 20, opacity: 0 },
  }));

  useEffect(() => {
    // Start dot fade-in immediately (invisible to visible, 600ms quick curve)
    dotApi.start({
      from: { opacity: 0, scale: 1 },
      to: { opacity: 1, scale: 1 },
      config: { duration: 600, easing: easings.easeInOutQuad },
    });

    // Start ring animation immediately (invisible → largest pulse over 600ms)
    ringApi.start({
      from: { width: 20, height: 20, opacity: 0 },
      to: async (animate) => {
        // Step 1: Invisible → largest pulse (20px) - 600ms "quick" curve
        await animate({
          width: 20,
          height: 20,
          opacity: 1,
          config: { duration: 600, easing: easings.easeInOutQuad },
        });
        // Step 2: 20px → 14px (1500ms ease-out)
        await animate({
          width: 14,
          height: 14,
          opacity: 1,
          config: { duration: 1500, easing: easings.easeOutCubic },
        });
        // Loop steps 2 and 3
        while (true) {
          // Step 3: 14px → 20px (1200ms ease-out, 100ms delay)
          await animate({
            width: 20,
            height: 20,
            opacity: 1,
            config: { duration: 1200, easing: easings.easeOutCubic },
            delay: 100,
          });
          // Back to step 2: 20px → 14px (1500ms ease-out)
          await animate({
            width: 14,
            height: 14,
            opacity: 1,
            config: { duration: 1500, easing: easings.easeOutCubic },
          });
        }
      },
    });
  }, [dotApi, ringApi]);

  return (
    <div className="absolute -top-0.5 -right-0.5 pointer-events-none z-10">
      {/* Container for centering */}
      <div className="relative w-4 h-4">
        {/* Outer pulsing ring - positioned absolutely from center */}
        <AnimatedDiv
          className="absolute top-1/2 left-1/2 rounded-full bg-token-purple-900/20"
          style={{
            width: ringSpring.width.to((w) => `${w}px`),
            height: ringSpring.height.to((h) => `${h}px`),
            transform: 'translate(-50%, -50%)',
            opacity: ringSpring.opacity,
            willChange: 'width, height, opacity',
          }}
        />
        {/* Inner dot - centered in container */}
        <AnimatedDiv
          className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full bg-token-purple-900"
          style={{
            marginLeft: '-4px',
            marginTop: '-4px',
            opacity: dotSpring.opacity,
            transform: dotSpring.scale.to((s) => `scale(${s})`),
          }}
        />
      </div>
    </div>
  );
}
